<template>
    <b-container fluid>
        <div v-if="wizardData">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
                <div class="pt-3 pb-md-0 pb-2">
                    <b> {{ wizardData.name }} - Details ({{ selectedLanguageLabel }}) </b>
                </div>

                <div class="d-flex align-items-center margin-top-1rem">
                    <div>
                        <img
                            v-for="l in languageOptions"
                            :key="l.code"
                            class="select-language-icon"
                            :src="`/static/common/images/flags/${l.code}.png`"
                            @click="setLanguage(l.code)"
                        />
                    </div>
                    <ai-scan-modal
                        :propertyId="propertyId"
                        :selectedLanguage="selectedLanguage"
                        :values="values"
                        :wizardData="wizardData"
                        :actions="parentActions"
                        @busy="handleBusy"
                    />
                </div>
            </div>
            <hr />
            <validation-observer
                v-if="values"
                ref="details_form"
                v-slot="{ meta, handleSubmit, values: formValues }"
                :initial-values="values"
                as="div"
                validate-on-mount
            >
                {{ setInvalid(!meta.valid) }}
                <b-row class="flex-column flex-md-row">
                    <b-col class="mb-2 pr-md-2">
                        <validation-provider
                            :name="isEnglish ? 'name' : 'languages.' + selectedLanguage + '.name'"
                            v-slot="{ field, errors }"
                            :rules="
                                isEnglish ? 'required|maxMessageLenOrEqual:100,Name' : 'maxMessageLenOrEqual:100,Name'
                            "
                            class="d-flex flex-column"
                        >
                            <form-input
                                v-bind="field"
                                class="mb-1"
                                :leftIcon="['fas', 'arrow-right']"
                                :floatingLabel="isEnglish ? 'Name' : `Name (${selectedLanguageLabel})`"
                                :error="errors[0]"
                            ></form-input>
                            <div class="d-flex justify-content-between mt-1 mb-1">
                                <span></span>
                                <b-button v-if="!isEnglish" variant="default" size="sm" @click="autoTranslate('name')"
                                    >Auto Translate</b-button
                                >
                            </div>
                        </validation-provider>
                    </b-col>
                    <b-col class="mb-2" v-if="wizardData.system_type !== 'inn'">
                        <validation-provider
                            :name="isEnglish ? 'marketing_info.title' : 'languages.' + selectedLanguage + '.title'"
                            v-slot="{ errors, field }"
                            :rules="'maxMessageLenOrEqual:80,Title'"
                            class="d-flex flex-column"
                        >
                            <form-input
                                v-bind="field"
                                class="mb-1"
                                :floatingLabel="
                                    isEnglish ? 'Marketing Title' : `Marketing Title (${selectedLanguageLabel})`
                                "
                                :leftIcon="['fas', 'arrow-right']"
                                :error="errors[0]"
                            ></form-input>
                            <div
                                class="d-flex flex-md-row flex-column justify-content-between align-items-end mb-1 mt-1"
                            >
                                <small class="text-secondary">
                                    (e.g. Stunning 4 bed. lakefront cottage in Tahoe)
                                </small>
                                <b-button variant="default" v-if="!isEnglish" size="sm" @click="autoTranslate('title')"
                                    >Auto Translate</b-button
                                >
                            </div>
                        </validation-provider>
                    </b-col>
                    <b-col md="6" class="mb-2" v-if="wizardData.system_type === 'inn' && isEnglish">
                        <validation-provider name="type" v-slot="{ errors, field }" rules="required">
                            <form-select
                                v-bind="field"
                                :options="propertyTypeOptions"
                                floatingLabel="Property Type"
                                :leftIcon="['fas', 'arrow-right']"
                                class="mb-1"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="flex-column flex-md-row mb-3 mt-2" v-show="isEnglish">
                    <b-col
                        md="6"
                        class="mb-2 pr-md-2"
                        v-if="wizardData.system_type !== 'roomtype' && wizardData.system_type !== 'inn'"
                    >
                        <validation-provider name="type" v-slot="{ errors, field }" rules="required">
                            <form-select
                                v-bind="field"
                                :options="propertyTypeOptions"
                                floatingLabel="Property Type"
                                :leftIcon="['fas', 'arrow-right']"
                                class="mb-1"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col
                        :md="wizardData.system_type === 'roomtype' ? 6 : 4"
                        class="mb-2 pr-md-2"
                        v-if="wizardData.system_type !== 'inn'"
                    >
                        <validation-provider name="how_big" v-slot="{ errors, field }" rules="validInteger">
                            <form-input
                                v-bind="field"
                                class="mb-1"
                                floatingLabel="How Big?"
                                :leftIcon="['fas', 'arrow-right']"
                                :error="errors[0]"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col
                        :md="wizardData.system_type === 'roomtype' ? 6 : 2"
                        class="mb-2"
                        v-if="wizardData.system_type !== 'inn'"
                    >
                        <validation-provider name="how_big_unit" v-slot="{ errors, field }">
                            <form-select
                                v-bind="field"
                                :options="howBigUnitOptions"
                                floatingLabel="Unit"
                                :leftIcon="['fas', 'arrow-right']"
                                class="mb-1"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="flex-column flex-md-row mb-3" v-if="wizardData.system_type !== 'inn'">
                    <b-col class="mb-2">
                        <validation-provider
                            :name="isEnglish ? 'marketing_info.teaser' : 'languages.' + selectedLanguage + '.teaser'"
                            v-slot="{ errors, field, value: marketingTeaserValue }"
                            rules="maxMessageLenOrEqual:250,Teaser"
                            class="d-flex flex-column"
                            validate-on-mount
                        >
                            <form-textarea
                                v-bind="field"
                                :rows="6"
                                :floatingLabel="
                                    isEnglish ? 'Marketing Teaser' : `Marketing Teaser (${selectedLanguageLabel})`
                                "
                                :leftIcon="['fas', 'note-sticky']"
                                :error="errors[0]"
                                class="mb-1"
                                maxlength="250"
                            />
                            <div class="d-flex flex-md-row flex-column justify-content-between align-items-end mt-1">
                                <small class="text-secondary">
                                    (250 Characters Max -
                                    {{ getMarketingTeaserRemainingCharacters(marketingTeaserValue) }}
                                    Characters Remaining)
                                </small>
                                <b-button v-if="!isEnglish" variant="default" size="sm" @click="autoTranslate('teaser')"
                                    >Auto Translate</b-button
                                >
                            </div>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="flex-column flex-md-row">
                    <b-col class="mb-2">
                        <validation-provider
                            :name="isEnglish ? 'description' : 'languages.' + selectedLanguage + '.description'"
                            v-slot="{ errors, field }"
                            :rules="isEnglish ? 'required' : ''"
                            class="d-flex flex-column"
                        >
                            <form-textarea
                                v-bind="field"
                                :rows="10"
                                :floatingLabel="isEnglish ? 'Description' : `Description (${selectedLanguageLabel})`"
                                :leftIcon="['fas', 'note-sticky']"
                                :error="errors[0]"
                                :class="{ 'mb-1': !isEnglish }"
                            />
                            <div class="d-flex justify-content-between mt-1">
                                <small class="text-secondary"> </small>
                                <b-button
                                    v-if="!isEnglish"
                                    variant="default"
                                    size="sm"
                                    @click="autoTranslate('description')"
                                    >Auto Translate</b-button
                                >
                            </div>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="flex-column flex-md-row mb-3">
                    <b-col class="mb-2">
                        <div class="d-flex flex-column">
                            <validation-provider
                                :name="
                                    isEnglish ? 'additional_info' : 'languages.' + selectedLanguage + '.additional_info'
                                "
                                v-slot="{ field }"
                            >
                                <form-ck-editor
                                    v-bind="field"
                                    class="mb-1"
                                    :label="
                                        isEnglish ? 'Additional Info' : `Additional Info (${selectedLanguageLabel})`
                                    "
                                    :config="ckEditorConfig"
                                ></form-ck-editor>
                            </validation-provider>
                            <div
                                class="d-flex flex-md-row flex-column justify-content-between align-items-end mb-1 mt-1"
                            >
                                <small class="text-secondary">
                                    (Additional Info - HTML allowed. Data here used with WordPress plug-in only)
                                </small>
                                <b-button
                                    v-if="!isEnglish"
                                    variant="default"
                                    size="sm"
                                    @click="autoTranslate('additional_info')"
                                    >Auto Translate</b-button
                                >
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <b-row v-show="isEnglish" class="mt-3 flex-column flex-md-row">
                    <b-col md="3" class="mb-2 pr-md-2" v-if="wizardData.system_type !== 'roomtype'">
                        <validation-provider
                            validate-on-mount
                            name="check_in"
                            v-slot="{ errors, field }"
                            rules="required"
                        >
                            <form-select
                                v-bind="field"
                                :options="cicoChoices"
                                floatingLabel="Arrival Time"
                                :leftIcon="['fas', 'clock']"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col md="3" class="mb-2 pr-md-2" v-if="wizardData.system_type !== 'roomtype'">
                        <validation-provider
                            validate-on-mount
                            name="check_out"
                            v-slot="{ errors, field }"
                            rules="required"
                        >
                            <form-select
                                v-bind="field"
                                :options="cicoChoices"
                                floatingLabel="Departure Time"
                                :leftIcon="['fas', 'clock']"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col md="6" class="mb-2">
                        <validation-provider name="no_booking_widget" v-slot="{ field }">
                            <form-select
                                v-bind="field"
                                floatingLabel="Booking Widget"
                                :options="bookingWidgetOptions"
                                :leftIcon="['fas', 'arrow-right']"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-show="isEnglish" class="flex-column flex-md-row">
                    <b-col md="6" class="mb-2 pr-md-2">
                        <validation-provider name="url" v-slot="{ errors, field }" rules="validUrl">
                            <form-input
                                v-bind="field"
                                floatingLabel="Property URL"
                                :error="errors[0]"
                                :leftIcon="['fas', 'link']"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col md="6" class="d-flex flex-column mb-2">
                        <validation-provider
                            name="tags"
                            v-slot="{ errors, field }"
                            rules="maxMessageLenOrEqual:512,Tags"
                        >
                            <form-input
                                v-bind="field"
                                floatingLabel="Tags"
                                :leftIcon="['fas', 'tag']"
                                class="mb-1"
                                :error="errors[0]"
                            />
                        </validation-provider>
                        <small class="text-secondary">
                            Note: if a tag contains a space, (e.g. lake house) enclose it in quotes.
                        </small>
                    </b-col>
                </b-row>
                <div v-if="wizardData.system_type !== 'inn' && isEnglish">
                    <div class="d-flex justify-content-between align-items-center">
                        <span class="pt-3"> <b>Accommodations </b></span>
                    </div>
                    <hr />
                    <b-row class="mt-2 flex-column flex-md-row">
                        <b-col class="mb-2 pr-md-2">
                            <validation-provider name="max_guests" v-slot="{ field }">
                                <form-select
                                    v-bind="field"
                                    floatingLabel="Maximum Guests"
                                    :options="maxGuestsOptions"
                                    :leftIcon="['fas', 'user']"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="mb-2">
                            <validation-provider name="baths" v-slot="{ handleChange, field }">
                                <form-select
                                    v-bind="field"
                                    floatingLabel="Baths"
                                    :options="bathsChoices"
                                    @change="
                                        (value) => {
                                            handleChange(value);
                                            synchronizeBathsBedroomsModal(value, 'baths');
                                        }
                                    "
                                    :leftIcon="['fas', 'bath']"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2 flex-column flex-md-row">
                        <b-col class="mb-2">
                            <validation-provider name="bedrooms" v-slot="{ handleChange, field }">
                                <form-select
                                    v-bind="field"
                                    floatingLabel="Bedrooms"
                                    :options="bedroomsChoices"
                                    :value="details.bedrooms"
                                    @change="
                                        (value) => {
                                            handleChange(value);
                                            synchronizeBathsBedroomsModal(value, 'bedrooms');
                                        }
                                    "
                                    :leftIcon="['fas', 'bed']"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="flex-md-row flex-column flex-column flex-md-row">
                        <b-col
                            v-for="(place, i) in values.places"
                            :key="place.id"
                            md="6"
                            :class="{ 'pr-md-2': i % 2 === 0 }"
                        >
                            <verify-accessibility-modal
                                v-if="initialData"
                                :modalId="'verify-accessibility-modal' + place.id"
                                :place="place"
                                :amenities="initialData.accessibility[place.sysname] || {}"
                                :placeAmenityPhoto="placeAmenityPhoto"
                                @photo-uploaded="fetchDetails"
                            />
                            <setup-beds-modal :modalId="'setup-beds-modal' + place.id" :place="place" />
                            <setup-bathroom-features-modal
                                :modalId="'setup-bathroom-features-modal' + place.id"
                                :place="place"
                            />
                            <b-card class="accomodation-card mb-2">
                                <template #header>
                                    {{ place.label }}
                                </template>

                                <b-card-body class="d-flex justify-content-between place-card-height">
                                    <div class="d-flex flex-column">
                                        <span v-if="showEditBedsButton(place)">
                                            {{ getSelectedBedroomsInPlace(place) || 'Sleeping arrangements' }}
                                        </span>
                                        <span v-if="showEditBathroomFeaturesButton(place)">
                                            {{ getSelectedBathroomFeaturesInPlace(place) || 'Bathroom Features' }}
                                        </span>
                                        <span v-if="details.airbnb_linked">
                                            {{ getAccessibilityText(place) }}
                                        </span>
                                    </div>
                                    <div class="d-flex flex-column">
                                        <responsive-button
                                            size="sm"
                                            v-if="showEditBedsButton(place)"
                                            @click="$bvModal.show('setup-beds-modal' + place.id)"
                                            variant="default"
                                            class="mb-1 place-setup-button"
                                            :icon="['fas', 'pen-to-square']"
                                            >{{ getPlaceBedsNumber(place) ? 'Edit' : 'Setup' }} Beds</responsive-button
                                        >
                                        <responsive-button
                                            size="sm"
                                            v-if="showEditBathroomFeaturesButton(place)"
                                            @click="$bvModal.show('setup-bathroom-features-modal' + place.id)"
                                            variant="default"
                                            class="mb-1 place-setup-button"
                                            :icon="['fas', 'pen-to-square']"
                                            >{{
                                                getPlaceBathroomFeaturesNumber(place) ? 'Edit' : 'Setup'
                                            }}
                                            Bathroom</responsive-button
                                        >
                                        <responsive-button
                                            v-if="details.airbnb_linked"
                                            size="sm"
                                            @click="$bvModal.show('verify-accessibility-modal' + place.id)"
                                            variant="default"
                                            class="place-setup-button"
                                            :icon="['fas', 'wheelchair']"
                                            >Accessibility</responsive-button
                                        >
                                    </div>
                                </b-card-body>
                            </b-card>
                        </b-col>
                    </b-row>
                    <hr />
                </div>
                <div v-if="wizardData.system_type !== 'inn'">
                    <div class="d-flex justify-content-between align-items-center">
                        <span class="pt-3"><b>Post-Booking Details</b></span>
                    </div>
                    <hr />
                    <b-row class="flex-wrap">
                        <b-col md="6" class="mb-2" v-show="isEnglish">
                            <validation-provider name="wifi_details.name" v-slot="{ field }">
                                <form-input
                                    v-bind="field"
                                    floatingLabel="Wi-Fi Network Name"
                                    :leftIcon="['fas', 'wifi']"
                                    class="mb-1"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col md="6" class="mb-2" v-show="isEnglish">
                            <validation-provider name="wifi_details.password" v-slot="{ field }">
                                <form-input
                                    v-bind="field"
                                    floatingLabel="Wi-Fi Network Password"
                                    :leftIcon="['fas', 'lock']"
                                    class="mb-1"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col md="6" class="mb-2">
                            <validation-provider
                                :name="isEnglish ? 'directions' : 'languages.' + selectedLanguage + '.directions'"
                                v-slot="{ field }"
                            >
                                <form-textarea
                                    v-bind="field"
                                    :rows="6"
                                    :floatingLabel="isEnglish ? 'Directions' : `Directions (${selectedLanguageLabel})`"
                                    :leftIcon="['fas', 'note-sticky']"
                                    class="mb-1"
                                />
                            </validation-provider>
                            <div>
                                <small class="text-secondary">
                                    Give guests detailed information on how to get to the property.
                                </small>
                            </div>
                        </b-col>
                        <b-col md="6" class="mb-2">
                            <validation-provider
                                :name="isEnglish ? 'house_manual' : 'languages.' + selectedLanguage + '.house_manual'"
                                v-slot="{ field }"
                            >
                                <form-textarea
                                    v-bind="field"
                                    :rows="6"
                                    :floatingLabel="
                                        isEnglish ? 'House Manual' : `House Manual (${selectedLanguageLabel})`
                                    "
                                    :leftIcon="['fas', 'note-sticky']"
                                    class="mb-1"
                                />
                            </validation-provider>
                            <div>
                                <small class="text-secondary">
                                    Give guests tips about your listing, such as how to access the internet or turn on
                                    the hot water.
                                </small>
                            </div>
                        </b-col>
                        <b-col md="6" class="d-flex flex-column mb-2" v-show="isEnglish">
                            <validation-provider name="check_in_options.category" v-slot="{ field }">
                                <form-select
                                    v-bind="field"
                                    floatingLabel="Check In Option"
                                    :options="checkInOptions"
                                    :leftIcon="['fas', 'arrow-right']"
                                    class="mt-2 mt-md-0"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row v-if="formValues.check_in_options.category > 0" class="flex-column flex-md-row">
                        <b-col class="mb-2 pr-md-2">
                            <validation-provider
                                :name="
                                    isEnglish
                                        ? 'check_in_options.instruction'
                                        : 'languages.' + selectedLanguage + '.instruction'
                                "
                                v-slot="{ errors, field }"
                                :rules="
                                    formValues.check_in_options.category == 7308
                                        ? 'required|maxMessageLenOrEqual:256,Instruction'
                                        : 'maxMessageLenOrEqual:256,Instruction'
                                "
                                validate-on-mount
                            >
                                <form-textarea
                                    v-bind="field"
                                    :rows="3"
                                    :floatingLabel="`Check In Instructions ${
                                        !isEnglish ? '(' + selectedLanguageLabel + ')' : ''
                                    }`"
                                    :leftIcon="['fas', 'tag']"
                                    class="mb-1"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="flex-column flex-md-row">
                        <b-col class="mb-2 pr-md-2">
                            <validation-provider
                                :name="isEnglish ? 'parking_info' : 'languages.' + selectedLanguage + '.parking_info'"
                                v-slot="{ field }"
                            >
                                <form-textarea
                                    v-bind="field"
                                    :rows="3"
                                    :floatingLabel="
                                        isEnglish
                                            ? 'Parking Information'
                                            : `Parking Information (${selectedLanguageLabel})`
                                    "
                                    :leftIcon="['fas', 'square-parking']"
                                    class="mb-1"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-card class="accomodation-card mb-2">
                        <template #header> Check Out Tasks </template>
                        <b-card-body>
                            <b-row class="mt-n2">
                                <b-col
                                    md="6"
                                    class="d-flex flex-column mt-2"
                                    v-for="(checkOutTask, idx) in values.check_out_tasks"
                                    :key="checkOutTask.category"
                                >
                                    <validation-provider
                                        :name="`check_out_tasks[${idx}]description`"
                                        v-slot="{ errors, field }"
                                        :rules="{
                                            required: checkOutTask.selected && checkOutTask.required,
                                            maxMessageLenOrEqual: [checkOutTask.maxLength, checkOutTask.label],
                                        }"
                                    >
                                        <form-textarea
                                            v-bind="field"
                                            rows="3"
                                            :label="checkOutTask.label"
                                            placeholder="Description"
                                            :leftButton="true"
                                            :disabled="!checkOutTask.selected"
                                            class="mb-1"
                                            :error="errors[0]"
                                        >
                                            <template #leftButton>
                                                <form-checkbox
                                                    v-model="checkOutTask.selected"
                                                    class="mb-0"
                                                    @change="(val) => onSelectCheckOutTask(val, checkOutTask)"
                                                />
                                            </template>
                                        </form-textarea>
                                    </validation-provider>
                                    <div>
                                        <small class="text-secondary">
                                            {{ checkOutTask.note }}
                                        </small>
                                    </div>
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </b-card>
                </div>
                <div
                    v-if="details && isEnglish && wizardData.system_type !== 'inn'"
                    class="toggle-accordion-button mt-4"
                    v-b-toggle="'accordion'"
                >
                    <div class="d-flex align-items-center">
                        <b class="d-block">Expectations For Guests (Airbnb Only) </b>
                        <span class="when-open ml-1"><font-awesome-icon :icon="['fas', 'circle-minus']" /></span>
                        <span class="when-closed ml-1"><font-awesome-icon :icon="['fas', 'circle-plus']" /></span>
                    </div>
                    <hr />
                </div>
                <b-collapse v-if="isEnglish && wizardData.system_type !== 'inn'" :id="'accordion'">
                    <b-row class="flex-wrap">
                        <b-col
                            md="6"
                            v-for="expectation in details.airbnb_expectation"
                            :key="expectation.type"
                            class="mb-2"
                        >
                            <form-input
                                :label="expectation.label"
                                placeholder="Description"
                                v-model="expectation.description"
                                @change="onChangeDetail"
                                :leftButton="true"
                                :disabled="!expectation.selected"
                                class="mb-1"
                            >
                                <template #leftButton>
                                    <form-checkbox
                                        v-model="expectation.selected"
                                        class="mb-0"
                                        @change="(val) => onSelectExpectation(val, expectation)"
                                    />
                                </template>
                            </form-input>
                            <div>
                                <small class="text-secondary">
                                    {{ expectation.note }}
                                </small>
                            </div>
                        </b-col>
                    </b-row>
                </b-collapse>
                <div v-if="!values.beds_migrated && initialData">
                    <div class="d-flex justify-content-between align-items-center">
                        <span class="pt-3"> <b>Beds (Deprecated) </b></span>
                    </div>
                    <hr />
                    <div>
                        <b-row class="" v-for="bed in values.beds" :key="bed.id">
                            <b-col md="8" class="">
                                <form-input
                                    :value="bed.label"
                                    disabled
                                    floatingLabel="Bed Type"
                                    :leftIcon="['fas', 'bed']"
                                />
                            </b-col>
                            <b-col md="4" class="">
                                <form-input
                                    :value="bed.qty"
                                    disabled
                                    floatingLabel="Quantity"
                                    :leftIcon="['fas', 'arrow-right']"
                                />
                            </b-col>
                        </b-row>
                    </div>
                </div>
                <b-button class="mt-2" @click="handleSubmit(_, onSave)" :disabled="!meta.valid" variant="primary"
                    >Save</b-button
                >
                <modal
                    :id="synchronizeBathsModalId"
                    title="Synchronize Places Confirm"
                    ok-only
                    @ok="synchronizePlaces"
                    @cancel="cancelSynchronize"
                >
                    Reducing the number of bedrooms or baths might delete Airbnb Accessibility verification photos. Are
                    you sure you want to continue?
                </modal>
            </validation-observer>
        </div>
    </b-container>
</template>

<script>
    import API from '../../../../api.jsx';
    import { RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT } from '../../../../endpoints';
    import FormCheckbox from '../../../generic/form-checkbox/form-checkbox.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
    import FormTextEditor from '../../../generic/form-text-editor/form-text-editor.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import { formatResponseError } from '../../../../utils/api';
    import Modal from '../../../generic/modal/modal.vue';
    import isEmpty from 'lodash/isEmpty';
    import { mapGetters } from 'vuex';
    import { cicoChoices, bathsChoices, bedroomsChoices, howBigUnitOptions, bookingWidgetOptions } from '../constants';
    import SetupBedsModal from './setup-beds-modal.vue';
    import SetupBathroomFeaturesModal from './setup-bathroom-features-modal.vue';
    import verifyAccessibilityModal from './verify-accessibility-modal.vue';
    import { generatePlaceAmenityPhoto, getExpectationLabel } from '../utils';
    import FormCkEditor from '../../../generic/form-ck-editor/form-ck-editor.vue';
    import AiScanModal from './ai-scan/ai-scan-modal.vue';

    export default {
        name: 'Details',
        components: {
            FormInput,
            FormSelect,
            FormTextarea,
            FormTextEditor,
            ResponsiveButton,
            SetupBathroomFeaturesModal,
            SetupBedsModal,
            Modal,
            verifyAccessibilityModal,
            FormCheckbox,
            FormCkEditor,
            AiScanModal,
        },
        data() {
            return {
                selectedLanguage: 'en',
                cicoChoices,
                bathsChoices,
                bedroomsChoices,
                howBigUnitOptions,
                bookingWidgetOptions,
                ckEditorConfig: {
                    toolbar: [
                        {
                            name: 'clipboard',
                            items: ['Cut', 'Copy', 'Paste', 'PasteText', '-', 'Undo', 'Redo'],
                        },
                        {
                            name: 'insert',
                            items: ['HorizontalRule', 'SpecialChar'],
                        },
                        { name: 'tools', items: ['Maximize'] },
                        { name: 'document', items: ['Source'] },
                        '/',
                        {
                            name: 'basicstyles',
                            items: ['Bold', 'Italic', 'Strike', '-', 'RemoveFormat'],
                        },
                        {
                            name: 'paragraph',
                            items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote'],
                        },
                        { name: 'styles', items: ['Styles', 'Format'] },
                    ],
                    resize_enabled: false,
                    removePlugins: 'elementspath',
                    enterMode: 2,
                    versionCheck: false,
                    shiftEnterMode: 1,
                    entities: false,
                    extraAllowedContent: 'iframe[*]',
                },
                expectationTypes: [],
                placeAmenityPhoto: {},
                synchronizeBathsModalId: 'synchronize-beds-modal',
                currrentChangedAmenity: '',
                baths: {
                    oldValue: '',
                    newValue: '',
                },
                bedrooms: {
                    oldValue: '',
                    newValue: '',
                },
                values: null,
                windowWidth: 0,
                windowHeight: 0,
            };
        },
        inject: ['propertyId', 'getModuleEndpointAndBody'],
        computed: {
            ...mapGetters('setupPropertyStore', ['wizardData', 'initialData', 'details']),
            languageOptions() {
                const englishLanguage = { id: null, code: 'en', name: 'English' };
                return this.initialData ? [englishLanguage, ...this.initialData.active_languages] : [englishLanguage];
            },
            checkInOptions() {
                return (
                    this.initialData &&
                    this.initialData.check_in_choices.map((item) => ({
                        value: item.id,
                        text: item.name,
                    }))
                );
            },
            selectedLanguageLabel() {
                return this.languageOptions.find((item) => item.code === this.selectedLanguage)?.name || '';
            },
            isEnglish() {
                return this.selectedLanguage === 'en';
            },
            propertyTypeOptions() {
                return (
                    this.initialData &&
                    this.initialData.property_type_choices.map((item) => ({
                        value: item.id,
                        text: item.name,
                    }))
                );
            },
            maxGuestsOptions() {
                return Array.from({ length: 200 }, (v, k) => k + 1).map((item) => ({
                    value: item,
                    text: item,
                }));
            },
            isMobile() {
                const md = 768;
                return this.windowWidth < md;
            },
            parentActions() {
                return {
                    onSave: this.onSave,
                    commitChangesToStore: this.commitChangesToStore,
                    fetchDetails: this.fetchDetails,
                    updateValues: this.updateValues,
                }
            }
        },
        async mounted() {
            await this.fetchDetails();
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
        },
        methods: {
            getMarketingTeaserRemainingCharacters(value) {
                return value ? 250 - value.length : 250;
            },
            getExpectationLabel,
            generateExpectationTypes(details) {
                if (details) {
                    const typesArr = Object.keys(details.expectation_types).map((item) => {
                        return {
                            label: this.getExpectationLabel(item),
                            type: item,
                            note: details.expectation_types[item] || '',
                            description: '',
                            name: '',
                            selected: false,
                        };
                    });
                    return typesArr.map((item) => {
                        const selectedItem = details.airbnb_expectation.find((e) => e.name === item.type);
                        if (selectedItem) {
                            return {
                                ...item,
                                ...selectedItem,
                                selected: true,
                            };
                        }
                        return {
                            ...item,
                        };
                    });
                }
            },
            onChangeDetail() {
                const details = JSON.parse(JSON.stringify(this.details));
                this.values = details;
                this.commitChangesToStore(details);
            },
            onSelectExpectation(value, expectation) {
                const details = JSON.parse(JSON.stringify(this.details));
                if (!value) {
                    details.airbnb_expectation = details.airbnb_expectation.map((item) => ({
                        ...item,
                        description: expectation.type === item.type ? '' : item.description,
                    }));
                }
                this.values = details;
                this.commitChangesToStore(details);
            },
            onSelectCheckOutTask(value, checkOutTask) {
                const details = JSON.parse(JSON.stringify(this.details));
                const index = details.check_out_tasks.findIndex((item) => item.category === checkOutTask.category);
                if (!value) {
                    if (index !== -1) {
                        this.$refs.details_form.setFieldValue(`check_out_tasks[${index}]selected`, false);
                        this.$refs.details_form.setFieldValue(`check_out_tasks[${index}]description`, '');
                        details.check_out_tasks[index] = {
                            ...details.check_out_tasks[index],
                            selected: false,
                            description: '',
                        };
                    }
                } else {
                    this.$refs.details_form.setFieldValue(`check_out_tasks[${index}]selected`, true);
                }
                this.values = details;
                this.commitChangesToStore(details);
            },
            showEditBedsButton(place) {
                return ['BEDROOM', 'LIVING_ROOM'].includes(place.sysname);
            },
            showEditBathroomFeaturesButton(place) {
                return ['FULL_BATHROOM', 'HALF_BATHROOM'].includes(place.sysname);
            },
            onResize() {
                this.windowWidth = window.innerWidth;
                this.windowHeight = window.innerHeight;
            },
            getPlaceBedsNumber(place) {
                return place.beds.reduce((prev, curr) => prev + curr.qty, 0);
            },
            getPlaceBathroomFeaturesNumber(place) {
                return place.bathroom_features.reduce((prev, curr) => prev + curr.qty, 0);
            },
            async autoTranslate(field) {
                try {
                    this.$parent.$emit('busy', true);
                    const { data } = await API.post(
                        `${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}translate/`,
                        {
                            code: this.selectedLanguage,
                            field,
                        }
                    );
                    const details = JSON.parse(
                        JSON.stringify({
                            ...this.values,
                        })
                    );
                    const teaserMaxLength = 250;
                    const titleMaxLength = 80;
                    if (field == 'teaser' && data.text.length > teaserMaxLength) {
                        details.languages[this.selectedLanguage].teaser = data.text.slice(0, teaserMaxLength - 1);
                        this.$refs.details_form.setFieldValue(
                            `languages.${this.selectedLanguage}.${field}`,
                            data.text.slice(0, teaserMaxLength - 1)
                        );
                        this.errorToast({
                            text: `Translation text exceeded the maximum of ${teaserMaxLength} characters, please correct it.`,
                        });
                    } else if (field == 'title' && data.text.length > titleMaxLength) {
                        details.languages[this.selectedLanguage].title = data.text.slice(0, titleMaxLength - 1);
                        this.$refs.details_form.setFieldValue(
                            `languages.${this.selectedLanguage}.${field}`,
                            data.text.slice(0, titleMaxLength - 1)
                        );
                        this.errorToast({
                            text: `Translation text exceeded the maximum of ${titleMaxLength} characters, please correct it.`,
                        });
                    } else {
                        details.languages[this.selectedLanguage][field] = data.text;
                        this.$refs.details_form.setFieldValue(`languages.${this.selectedLanguage}.${field}`, data.text);
                        this.successToast({
                            text: 'Field successfully translated.',
                        });
                    }
                    this.values = details;

                    this.commitChangesToStore(details);
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not translate' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            setInvalid(value) {
                this.$emit('setInvalid', value);
            },
            commitChangesToStore(values) {
                this.$store.commit('setupPropertyStore/gotDetails', values);
            },
            async onSave(values) {
                try {
                    this.$parent.$emit('busy', true);
                    const module = JSON.parse(JSON.stringify(this.getModuleEndpointAndBody()));

                    values.check_out_tasks = values.check_out_tasks
                        .filter((item) => item.selected)
                        .map((item) => ({
                            category: item.category,
                            description: item.description,
                        }));
                    const body = {
                        ...values,
                        airbnb_expectation: module.body.airbnb_expectation,
                    };
                    module.body = body;
                    const response = await this.$store.dispatch('setupPropertyStore/saveModule', module);
                    if (!response.data.check_in_options.category) {
                        response.data.check_in_options.category = -1;
                    }
                    response.data.airbnb_expectation = this.generateExpectationTypes(response.data);
                    response.data.check_out_tasks = this.generateCheckOutTasks(response.data);
                    this.values = response.data;

                    this.commitChangesToStore(response.data);
                    this.$store.commit('setupPropertyStore/gotPropertyName', response.data.name);
                    this.successToast({
                        text: 'Property details successfully saved',
                    });
                } catch (e) {
                    var { generic, fields } = formatResponseError(e);
                    if ('name' in fields) {
                        generic = 'Invalid property name. ' + fields['name'];
                    }
                    if (Object.keys(fields).length > 0) {
                        for (const field in fields) {
                            this.errorToast({ text: field + ' ' + fields[field] });
                        }
                    } else {
                        this.errorToast({ text: generic || 'Could not save details' });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.details_form.setErrors(fields);
                    }
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            setLanguage(language) {
                this.selectedLanguage = language;
                this.$refs.details_form.setValues(this.values);
            },
            getSelectedBedroomsInPlace(place) {
                const text = place.beds
                    .filter((item) => item.qty)
                    .map((item) => `${item.qty} ${item.name}`)
                    .join(', ');
                return text ? `Beds: ${text}` : '';
            },
            getSelectedBathroomFeaturesInPlace(place) {
                const text = place.bathroom_features
                    .filter((item) => item.qty)
                    .map((item) => `${item.qty} ${item.name}`)
                    .join(', ');
                return text ? `Bathroom: ${text}` : '';
            },
            cancelSynchronize() {
                const details = JSON.parse(
                    JSON.stringify({
                        ...this.values,
                    })
                );
                details[this.currrentChangedAmenity] = this[this.currrentChangedAmenity].oldValue;
                this.values = details;
                this.commitChangesToStore(details);
                this[this.currrentChangedAmenity] = {
                    oldValue: '',
                    newValue: '',
                };
                this.currrentChangedAmenity = '';
            },
            async synchronizePlaces() {
                try {
                    this.$parent.$emit('busy', true);
                    const { data } = await API.post(
                        `${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}synchronize-places/`,
                        {
                            bedrooms: Number(this.values.bedrooms),
                            baths: Number(this.values.baths),
                        }
                    );
                    if (data) {
                        const details = JSON.parse(JSON.stringify(this.values));
                        details.places = data.places;
                        details.baths = data.baths;
                        details.bedrooms = data.bedrooms;
                        this.values = details;
                        this.commitChangesToStore(details);
                    }
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not syncronize places',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            synchronizeBathsBedroomsModal(value, field) {
                const details = JSON.parse(
                    JSON.stringify({
                        ...this.values,
                    })
                );
                const oldValue = details[field];
                const newValue = value;
                this.currrentChangedAmenity = field;
                this[field] = {
                    oldValue,
                    newValue,
                };
                details[field] = newValue;
                this.values = details;
                this.commitChangesToStore(details);
                if (this.values.airbnb_linked && !isEmpty(this.placeAmenityPhoto)) {
                    if (parseFloat(newValue) < parseFloat(oldValue)) {
                        this.$bvModal.show(this.synchronizeBathsModalId);
                    } else {
                        this.synchronizePlaces();
                    }
                } else {
                    this.synchronizePlaces();
                }
            },
            getAccessibilityText(place) {
                const text = [];

                if (this.placeAmenityPhoto[place.id] !== undefined) {
                    for (let item in this.placeAmenityPhoto[place.id]) {
                        text.push(item);
                        if (text.length > 0 && Object.keys(this.placeAmenityPhoto[place.id]).length > 1) {
                            text.push('...');
                            break;
                        }
                    }

                    if (text.length > 0) {
                        return 'Accessibility: ' + text.join(', ');
                    }
                }
                return 'Accessibility: N/A';
            },
            onInputChange(value, field, isLanguages, nestedField) {
                if (isLanguages && this.isEnglish && nestedField) {
                    this.values[nestedField][field] = value;
                } else if (isLanguages && this.isEnglish && !nestedField) {
                    this.values[field] = value;
                } else if (isLanguages && !this.isEnglish) {
                    this.values.languages[this.selectedLanguage][field] = value;
                } else if (!isLanguages && nestedField) {
                    this.values[nestedField][field] = value;
                } else if (!isLanguages) {
                    this.values[field] = value;
                }
                this.commitChangesToStore(JSON.parse(JSON.stringify(this.values)));
            },
            generatePlaceAmenityPhoto,
            generateCheckOutTasks(details) {
                if (details) {
                    return details.check_out_task_categories.map((taskCategory) => {
                        const checkOutTask = details.check_out_tasks.find(
                            (task) => task.category == taskCategory.category
                        );
                        return {
                            label: taskCategory.label,
                            category: taskCategory.category,
                            description: checkOutTask ? checkOutTask.description : '',
                            note: taskCategory.note,
                            maxLength: taskCategory.max_length,
                            selected: checkOutTask ? true : false,
                            required: taskCategory.required,
                        };
                    });
                }
            },
            async fetchDetails() {
                try {
                    this.$parent.$emit('busy', true);
                    const data = await this.$store.dispatch('setupPropertyStore/getDetails', {
                        propertyId: this.propertyId,
                    });
                    if (data) {
                        data.airbnb_expectation = this.generateExpectationTypes(data);
                        this.placeAmenityPhoto = this.generatePlaceAmenityPhoto(data);
                        data.check_out_tasks = this.generateCheckOutTasks(data);
                        if (!data.check_in_options.category) {
                            data.check_in_options.category = -1;
                        }
                        this.values = data;
                        this.commitChangesToStore(data);
                    }
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch details' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            handleBusy(isBusy) {
                console.log('handleBusy', isBusy);
                this.$parent.$emit('busy', isBusy);
            },
            updateValues(values) {
                this.values = values;
            }
        },
    };
</script>

<style lang="scss" scoped>
    .select-language-icon {
        margin-right: 0.5rem;
        cursor: pointer;
    }

    .accomodation-card {
        :deep(.card-header) {
            padding: 10px 15px !important;
            border-bottom: 1px solid #ddd !important;
            background-color: #f5f5f5 !important;
            font-weight: bold;
        }
        :deep(.card-body) {
            padding: 10px 10px;
        }
    }
    .toggle-accordion-button {
        cursor: pointer;
        :hover {
            color: #0d47a1;
        }
        .when-open {
            display: block;
        }
        .when-closed {
            display: none;
        }
    }
    :deep(.toggle-accordion-button.collapsed) {
        .when-closed {
            display: block;
        }
        .when-open {
            display: none;
        }
    }
    :deep(.place-setup-button) {
        min-width: 140px;
    }
    :deep(.place-card-height) {
        min-height: 45px !important;
    }
</style>
